import React, { useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useTranslation } from "react-i18next";
import JsonData from "../data/data.json";

/* O slick clona os slides para o loop infinito, então qualquer <h1> dentro de
   um slide vira dois no DOM. O h1 da página mora fora do slider. */
const HeroPageHeading = () => {
  const { t } = useTranslation("common");
  return <h1 className="sr-only">{t("meta.title")}</h1>;
};

/* O slick injeta currentSlide/slideCount na seta customizada; se ela for um
   <button> puro, viram atributos DOM inválidos e o React reclama. */
const SlideArrow = ({ currentSlide, slideCount, ...rest }) => (
  <button type="button" {...rest} />
);

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const openInNewTab = () => {
  const domain = JsonData?.Header?.nameBankDomain || "ciryon";
  window.open(`https://app.${domain}.com.br/register`, "_blank", "noreferrer");
};

const SlideItem = ({ slide, index, total, openAccountLabel, slogan, isSingle }) => {
  return (
    <div
      className={`slide-wrapper${isSingle ? " hero--single" : ""}`}
      role="group"
      aria-roledescription="slide"
      aria-label={`${index + 1} / ${total}`}
    >
      <div className="slide-image">
        {/* Decorativa: todo o texto do slide já está no DOM abaixo. */}
        <img
          src={slide.image}
          alt=""
          aria-hidden="true"
          className="slide-img"
          loading={index === 0 ? "eager" : "lazy"}
          decoding="async"
        />
        <div className="hero__overlay" aria-hidden="true" />

        <div className="hero__content">
          <div className="hero__inner">
            <p className="hero__slogan">{slogan}</p>
            <span className="hero__eyebrow" aria-hidden="true" />
            <h2 className="hero__title">
              <span className="hero__title-line">{slide.title}</span>
              {slide.protectedText && (
                <span className="hero__title-accent">{slide.protectedText}</span>
              )}
            </h2>
            <p className="hero__subtitle">{slide.subtitle}</p>
            <button
              type="button"
              className="btn btn-custom btn-lg hero__cta"
              onClick={openInNewTab}
            >
              {openAccountLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Slidershow = () => {
  const { t, i18n } = useTranslation("landing");
  const { t: tCommon } = useTranslation("common");
  const brand = JsonData?.About?.nameBank || "CIRYON";
  const slides = t("slides.items", { returnObjects: true, brand });
  const openAccountLabel = t("slides.openAccount");
  const slideList = Array.isArray(slides) ? slides : [];
  const prevLabel = t("slides.prev");
  const nextLabel = t("slides.next");
  const slogan = tCommon("slogan");

  const settings = useMemo(() => {
    const reduce = prefersReducedMotion();
    return {
      dots: true,
      infinite: true,
      speed: reduce ? 0 : 700,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: !reduce,
      autoplaySpeed: 6000,
      pauseOnHover: true,
      pauseOnFocus: true,
      arrows: true,
      cssEase: "cubic-bezier(0.22, 1, 0.36, 1)",
      adaptiveHeight: false,
      // O slick rotula as setas como "Previous"/"Next" em inglês.
      prevArrow: <SlideArrow aria-label={prevLabel} />,
      nextArrow: <SlideArrow aria-label={nextLabel} />,
      customPaging: (i) => (
        <button type="button" aria-label={`${i + 1} / ${slideList.length}`} />
      ),
    };
  }, [slideList.length, prevLabel, nextLabel]);

  if (slideList.length === 0) return null;

  if (slideList.length === 1) {
    return (
      <div className="slideshow">
        <HeroPageHeading />
        <SlideItem
          slide={slideList[0]}
          index={0}
          total={1}
          openAccountLabel={openAccountLabel}
          slogan={slogan}
          isSingle
        />
      </div>
    );
  }

  return (
    <div className="slideshow">
      <HeroPageHeading />
      {/* Remonta o slick ao trocar idioma — clones do loop mantêm texto antigo. */}
      <Slider key={i18n.language} {...settings}>
        {slideList.map((slide, index) => (
          <SlideItem
            key={slide.image || index}
            slide={slide}
            index={index}
            total={slideList.length}
            openAccountLabel={openAccountLabel}
            slogan={slogan}
          />
        ))}
      </Slider>
    </div>
  );
};
